import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { ContentLayout } from "./ContentLayout";
import { WelcomeStep } from "./WelcomeStep/WelcomeStep";
import { localize } from "client_legacy/ndaw/culture";
import {
	BusyIndicator,
	Button,
	Form,
	FormattedText,
	FormItem,
	TextInput,
	ValidationError
} from "client/Components";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	hasMaximumEntries,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { MAX_TAG_COUNT, UserTagSelect } from "../Settings/UserProfile/UserTagSelect";
import { useLoadUserProfileViewModel } from "../Settings/UserProfile/useLoadUserProfileViewModel";
import {
	saveProfessionalInfoStep,
	SaveUserProfessionalInfoModel,
	UserIdentityModel,
	UserProfileEditRestrictions,
	UserProfileModel,
	WorkingDaysModel
} from "client/Api/UserProfile/service";
import { IInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import {
	DayOfWeek,
	formatDayOfWeek,
	getFirstWeekDay,
	getWeekDays
} from "client/DateAndTime/culture";
import styles from "./ProfessionalInfoStep.module.less";
import image from "Content/Images/professional-info-step.svg";
import { useUserProfileStore } from "../Settings/UserProfile/userProfileStore";
import { TagKind } from "client/Api/Tags/tags";

export type ProfessionalInfoStepProps = {
	onNext(): void;
};

export const ProfessionalInfoStep = (props: ProfessionalInfoStepProps): ReactElement => {
	const {
		onNext
	} = props;

	const [formData, setFormData] = useState<SaveUserProfessionalInfoModel>(EMPTY_FORM_DATA);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation = useUnmountedCancellation();
	const setCurrentUserIdentity: (patch: Partial<Omit<UserIdentityModel, "userId">>) => void = useUserProfileStore(x => x.setCurrentUserIdentity);

	const formHandler: IFormHandler<SaveUserProfessionalInfoModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const {
		viewModel,
		status: loadStatus
	} = useLoadUserProfileViewModel();

	const editRestrictions: UserProfileEditRestrictions | undefined = viewModel?.editRestrictions;

	useEffect(
		(): void => {
			if (!viewModel) {
				return;
			}

			const saveModel: SaveUserProfessionalInfoModel = convertToSaveModel(viewModel.model);

			setFormData(saveModel);
		},
		[viewModel]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (loadStatus !== "ready" || isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				await saveProfessionalInfoStep(formData, globalCancellation);

				const {
					jobTitle
				} = formData;

				setCurrentUserIdentity({
					jobTitle
				});

				setIsBusy(false);
				onNext();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
				setIsBusy(false);
			}
		},
		[
			alertManager,
			formHandler.validation,
			globalCancellation,
			isBusy,
			loadStatus,
			onNext,
			formData,
			setCurrentUserIdentity
		]
	);

	return (
		<>
			<WelcomeStep.Left
				trackingId="professional-information-step"
			>
				{isBusy || loadStatus === "loading" && (
					<BusyIndicator />
				)}
				<ContentLayout
					title={localize("_UserInfoStepTitle")}
					explanation={
						formHandler.value
							? (
								<FormattedText
									text={localize("_UserProfessionalInfoStepExplanation")}
									thingsToReplace={[]}
								/>
							)
							: undefined
					}
				>
					<Form onSubmit={saveHandler}>
						<FormItem
							label={localize("_JobTitle")}
						>
							<TextInput
								{...formHandler.for("jobTitle")
									.attachToTextInput()}
								isDisabled={editRestrictions?.job.isDisabled}
							/>
							<ValidationError
								{...formHandler.for("jobTitle")
									.attachToValidationError()}
							/>
						</FormItem>
						<FormItem
							label={localize("_Department")}
						>
							<TextInput
								{...formHandler.for("department")
									.attachToTextInput()}
								isDisabled={editRestrictions?.department.isDisabled}
							/>
							<ValidationError
								{...formHandler.for("department")
									.attachToValidationError()}
							/>
						</FormItem>
						<FormItem
							label={localize("_OfficeLocation")}
						>
							<TextInput
								{...formHandler.for("officeLocation").attachToTextInput()}
								isDisabled={editRestrictions?.officeLocation
									.isDisabled
								}
							/>

							<ValidationError {...formHandler.for("officeLocation").attachToValidationError()} />
						</FormItem>
						<UserTagSelect
							label={localize("_Projects")}
							attachable={formHandler.for("projects")}
							kind={TagKind.UserProjects}
							placeholder={localize("_AddATag")}
						/>
						<UserTagSelect
							label={localize("_SkillsAndExpertise")}
							attachable={formHandler.for("skills")}
							kind={TagKind.UserSkillsExp}
							placeholder={localize("_AddATag")}
						/>
						<FormItem label={localize("_WorkingDays")}>
							<WorkingDaysInput
								attachable={formHandler.for("workingDays")}
							/>
							<ValidationError
								{...formHandler.for("workingDays")
									.attachToValidationError()
								}
							/>
						</FormItem>
					</Form>

					<ContentLayout.Buttons
						onContinue={saveHandler}
						onSkip={onNext}
					/>
				</ContentLayout>
			</WelcomeStep.Left>
			<WelcomeStep.Right
				imageUrl={image}
				text={localize("_ProvidingDetailedInformationWillHelpYourColleaguesInfo")}
			/>
		</>
	);
};
ProfessionalInfoStep.displayName = "ProfessionalInfoStep";

const EMPTY_FORM_DATA = {
	jobTitle: "",
	department: "",
	officeLocation: "",
	projects: [],
	skills: [],
	workingDays: {
		monday: false,
		tuesday: false,
		wednesday: false,
		thursday: false,
		friday: false,
		saturday: false,
		sunday: false
	}
};

const validationConfiguration: IValidationConfiguration<SaveUserProfessionalInfoModel> =
	createValidationConfiguration<SaveUserProfessionalInfoModel>();

validationConfiguration.forScalar("department")
	.add(
		doesNotExceedLength(64)
	);

validationConfiguration.forScalar("officeLocation")
	.add(
		doesNotExceedLength(128)
	);

validationConfiguration.forCollection("projects")
	.add(
		isNotNullNorUndefined(),
		hasMaximumEntries(MAX_TAG_COUNT)
	);

validationConfiguration.forCollection("skills")
	.add(
		isNotNullNorUndefined(),
		hasMaximumEntries(MAX_TAG_COUNT)
	);

function convertToSaveModel(
	model: UserProfileModel
): SaveUserProfessionalInfoModel {
	return {
		jobTitle: model.jobTitle,
		department: model.department,
		officeLocation: model.officeLocation,
		projects: model.projects,
		skills: model.skills,
		workingDays: model.workingDays,
	};
}

const firstWeekDay: DayOfWeek = getFirstWeekDay();
const weekDays: readonly DayOfWeek[] = getWeekDays(firstWeekDay);

type WorkingDaysInputProps = {
	attachable: IValueInputAttachable<WorkingDaysModel>;
};

const WorkingDaysInput = (props: WorkingDaysInputProps): ReactElement => {
	const {
		attachable
	} = props;

	const clickHandler = useCallback(
		(handler: IInputAttachable<boolean>): void => {
			handler.reducer
				.setValue(!handler.value);
			handler.validation
				.triggerInputFinished();
		},
		[]
	);

	return (
		<ul className={styles.weekdaysList}>
			{weekDays.map(d => {
				const handler: IInputAttachable<boolean> = attachable.for(dayOfWeekToPropertyMap[d]);

				return (
					<li key={d}>
						<Button
							onClick={() => clickHandler(handler)}
							text={formatDayOfWeek(d, "full")}
							kind={handler.value
								? "primary"
								: "secondary"
							}
							trackingId={handler
								.attachToCheckbox()
								.trackingId}
						/>
					</li>
				);
			})}
		</ul>
	);
};
WorkingDaysInput.displayName = "WorkingDaysInput";

const dayOfWeekToPropertyMap: {[key in DayOfWeek]: keyof WorkingDaysModel} = {
	[DayOfWeek.Monday]: "monday",
	[DayOfWeek.Tuesday]: "tuesday",
	[DayOfWeek.Wednesday]: "wednesday",
	[DayOfWeek.Thursday]: "thursday",
	[DayOfWeek.Friday]: "friday",
	[DayOfWeek.Saturday]: "saturday",
	[DayOfWeek.Sunday]: "sunday"
};